import { useEffect, useState } from 'react';
import { Dialog, DialogContent, HiddenDialogTitle } from '~/components/ui/Dialog';
import { BoltLogoSimple } from '~/components/ui/logos/BoltLogoSimple';
import { sendAnalyticsEvent } from '~/lib/analytics.client';
import { PRIVACY_POLICY_URL, TERMS_OF_SERVICE_URL } from '~/lib/urls';
import { LoginButton } from '~/routes/login';
import { AnalyticsAction, AnalyticsTrackEvent } from '~/types/analytics';

type OauthProvider = 'github' | 'google_oauth2' | 'login_password';

const LAST_LOGIN_PROVIDER_KEY = 'lastLoginProvider';

interface Props {
  open: boolean;
  onClose: () => void;
  onLoginSuccess: () => void;
}

const LastUsedBadge = () => (
  <span className="absolute bg-bolt-ds-surfaceTwo border border-bolt-ds-border-divider px-2 right-2 rounded-full text-[10px] top-[-10px] group-hover:brightness-130">
    Last used
  </span>
);

export const LoginDialog = ({ open, onClose, onLoginSuccess }: Props) => {
  const [lastUsedProvider, setLastUsedProvider] = useState<OauthProvider | null>(null);

  // read from localStorage on mount
  useEffect(() => {
    const stored = localStorage.getItem(LAST_LOGIN_PROVIDER_KEY) as OauthProvider | null;

    if (stored) {
      setLastUsedProvider(stored);
    }
  }, []);

  const handleLoginSuccess = (provider: OauthProvider) => {
    localStorage.setItem(LAST_LOGIN_PROVIDER_KEY, provider);
    onLoginSuccess();
  };
  const onCloseWrapper: typeof onClose = (...args) => {
    sendAnalyticsEvent({
      action: AnalyticsAction.Track,
      payload: {
        event: AnalyticsTrackEvent.LoginDialogClosed,
      },
      isAnonymous: true,
    });
    return onClose(...args);
  };

  useEffect(() => {
    if (open) {
      sendAnalyticsEvent({
        action: AnalyticsAction.Track,
        payload: {
          event: AnalyticsTrackEvent.LoginDialogOpen,
        },
        isAnonymous: true,
      });
    }
  }, [open]);

  return (
    <Dialog open={open} onClose={onCloseWrapper} maxWidth={530}>
      <HiddenDialogTitle>Login</HiddenDialogTitle>
      <DialogContent className="py-10 px-10 sm:py-19 sm:px-21">
        <div className="flex items-start justify-center pb-10">
          <BoltLogoSimple width={120} height={60} />
        </div>

        <div className="flex flex-col items-center w-full text-center">
          <span className="text-sm text-bolt-ds-textSecondary mb-8">
            To use Bolt you must log into an existing account or create one using one of the options below
          </span>

          <div className="flex flex-col gap-4 w-full">
            <LoginButton
              className="group"
              onLoginSuccess={() => handleLoginSuccess('google_oauth2')}
              provider="google_oauth2"
              startIcon="i-ph:google-logo-bold"
              variant="secondary"
            >
              Sign in with Google
              {lastUsedProvider === 'google_oauth2' && <LastUsedBadge />}
            </LoginButton>

            <LoginButton
              className="group"
              onLoginSuccess={() => handleLoginSuccess('github')}
              provider="github"
              startIcon="i-bolt:logos-github?mask"
              variant="secondary"
            >
              Sign in with GitHub
              {lastUsedProvider === 'github' && <LastUsedBadge />}
            </LoginButton>

            <LoginButton
              className="group"
              onLoginSuccess={() => handleLoginSuccess('login_password')}
              variant="secondary"
              provider="login_password"
            >
              Sign in with email and password
              {lastUsedProvider === 'login_password' && <LastUsedBadge />}
            </LoginButton>
          </div>

          <p className="mt-6 text-sm text-bolt-ds-textTertiary">
            By signing in, you accept the{' '}
            <a
              href={TERMS_OF_SERVICE_URL}
              className="hover:text-bolt-elements-textPrimary transition-theme underline"
              target="_blank"
            >
              Terms of Service
            </a>{' '}
            and acknowledge our{' '}
            <a
              href={PRIVACY_POLICY_URL}
              className="hover:text-bolt-elements-textPrimary transition-theme underline"
              target="_blank"
            >
              Privacy Policy
            </a>
            .
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
